/**
 * One-time læknanúmer entry, shown in the blood-test order sheet when ordering
 * is enabled but the doctor's number isn't on file (the sheet does the gating).
 * Write-once: there is no edit view afterwards, so the number is read back for
 * confirmation before it is saved.
 */

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { IdCard, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  confirmNumberClasses,
  confirmRowClasses,
  confirmTextClasses,
  promptDescriptionClasses,
  promptErrorClasses,
  promptFieldClasses,
  promptFormClasses,
} from '../laeknanumer.styles';
import { type LaeknanumerConfirmProps, type LaeknanumerFormValues, laeknanumerSchema } from '../laeknanumer.types';
import { useSetLaeknanumer } from '../useSetLaeknanumer';

export const LaeknanumerPrompt = () => {
  const mutation = useSetLaeknanumer();
  const [pending, setPending] = useState<string | null>(null);
  const form = useForm<LaeknanumerFormValues>({
    resolver: zodResolver(laeknanumerSchema),
    defaultValues: { laeknanumer: '' },
  });

  const onSubmit = (values: LaeknanumerFormValues) => setPending(values.laeknanumer);

  return (
    <Alert>
      <IdCard className="h-4 w-4" />
      <AlertTitle>Vinsamlegast skráðu læknanúmerið þitt</AlertTitle>
      <AlertDescription className={promptDescriptionClasses}>
        <p>Læknanúmerið þarf til að panta blóðprufur. Þú skráir það aðeins einu sinni.</p>
        {pending !== null ? (
          <LaeknanumerConfirm
            laeknanumer={pending}
            isSaving={mutation.isPending}
            onConfirm={() => mutation.mutate(pending)}
            onEdit={() => {
              mutation.reset();
              setPending(null);
            }}
          />
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className={promptFormClasses}>
              <FormField
                control={form.control}
                name="laeknanumer"
                render={({ field }) => (
                  <FormItem className={promptFieldClasses}>
                    <FormLabel>Læknanúmer</FormLabel>
                    <FormControl>
                      <Input {...field} inputMode="numeric" autoComplete="off" maxLength={10} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit">Vista</Button>
            </form>
          </Form>
        )}
        {mutation.isError && <p className={promptErrorClasses}>{mutation.error.message}</p>}
      </AlertDescription>
    </Alert>
  );
};

const LaeknanumerConfirm = ({ laeknanumer, isSaving, onConfirm, onEdit }: LaeknanumerConfirmProps) => (
  <div className={confirmRowClasses}>
    <p className={confirmTextClasses}>
      Læknanúmer: <span className={confirmNumberClasses}>{laeknanumer}</span>
    </p>
    <Button variant="outline" onClick={onEdit} disabled={isSaving}>
      Breyta
    </Button>
    <Button onClick={onConfirm} disabled={isSaving}>
      {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      Staðfesta
    </Button>
  </div>
);
